import axios from "axios";
import { API_TIMEOUT_MS } from "./constants";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  timeout: API_TIMEOUT_MS,
  withCredentials: true, // send session_token cookie on every request
});

// Global 401 handler — swap the raw "Not authenticated" backend detail for a
// friendlier prompt that tells the user exactly what to do. `/auth/me` is
// excluded because it's polled to detect the anonymous state at startup.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err.response?.status;
    const path = err.config?.url || "";
    if (status === 401 && !path.endsWith("/auth/me")) {
      err.response.data = {
        ...(err.response.data || {}),
        detail: "You must be signed in to do that — click \"Sign in with Google\" in the left sidebar.",
      };
    }
    return Promise.reject(err);
  },
);

// ------------------ Auth ------------------
export const authMe = () => api.get("/auth/me").then((r) => r.data);
export const authSession = (session_id) =>
  api.post("/auth/session", { session_id }).then((r) => r.data);
export const authLogout = () =>
  api.post("/auth/logout").then((r) => r.data);

export const scrapeUrl = (url) => api.post("/scrape", { url }).then((r) => r.data);

export const generateAds = (payload) =>
  api.post("/generate-ads", payload).then((r) => r.data);

export const listLinks = () => api.get("/affiliate-links").then((r) => r.data);
export const createLink = (payload) =>
  api.post("/affiliate-links", payload).then((r) => r.data);
export const deleteLink = (id) =>
  api.delete(`/affiliate-links/${id}`).then((r) => r.data);
export const trackClick = (id) =>
  api.post(`/affiliate-links/${id}/track-click`).then((r) => r.data);

export const listAdSets = () => api.get("/ad-sets").then((r) => r.data);
export const getAdSet = (id) =>
  api.get(`/ad-sets/${id}`).then((r) => r.data);
export const adSetPerformance = (id) =>
  api.get(`/ad-sets/${id}/performance`).then((r) => r.data);
export const regenerateVariant = (id, payload) =>
  api.post(`/ad-sets/${id}/regenerate-variant`, payload).then((r) => r.data);
export const refreshAroundWinner = (id, payload) =>
  api.post(`/ad-sets/${id}/refresh-around-winner`, payload).then((r) => r.data);
export const updateVariantText = (id, payload) =>
  api.patch(`/ad-sets/${id}/variants/text`, payload).then((r) => r.data);
export const updateAdFields = (id, payload) =>
  api.patch(`/ad-sets/${id}/ad-fields`, payload).then((r) => r.data);
export const autoFixVariant = (id, payload) =>
  api.post(`/ad-sets/${id}/variants/auto-fix`, payload).then((r) => r.data);
export const analyticsSummary = () =>
  api.get("/analytics/summary").then((r) => r.data);

// Compliance
export const listPolicies = () =>
  api.get("/compliance/policies").then((r) => r.data);
export const createPolicy = (formData) =>
  api
    .post("/compliance/policies", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((r) => r.data);
export const deletePolicy = (id) =>
  api.delete(`/compliance/policies/${id}`).then((r) => r.data);
export const runComplianceCheck = (adSetId, policyIds) =>
  api
    .post(`/ad-sets/${adSetId}/compliance-check`, { policy_ids: policyIds })
    .then((r) => r.data);
export const updateEnforcement = (adSetId, payload) =>
  api.post(`/ad-sets/${adSetId}/enforcement`, payload).then((r) => r.data);

// Surveys
export const listSurveys = () => api.get("/surveys").then((r) => r.data);
export const createSurvey = (payload) =>
  api.post("/surveys", payload).then((r) => r.data);
export const deleteSurvey = (id) =>
  api.delete(`/surveys/${id}`).then((r) => r.data);
export const getSurvey = (id) => api.get(`/surveys/${id}`).then((r) => r.data);
export const listSurveySubmissions = (id) =>
  api.get(`/surveys/${id}/submissions`).then((r) => r.data);
export const getSurveyForClick = (adSetId, platform, variantId) =>
  api
    .get(`/surveys/for-click/${adSetId}/${platform}/${variantId}`)
    .then((r) => r.data);
export const submitSurvey = (payload) =>
  api.post("/surveys/submit", payload).then((r) => r.data);

// Conversions
export const logConversion = (payload) =>
  api.post("/conversions", payload).then((r) => r.data);
export const listConversions = (adSetId) =>
  api.get(`/conversions/ad-set/${adSetId}`).then((r) => r.data);
export const conversionSetup = (adSetId) =>
  api.get(`/conversions/setup/${adSetId}`).then((r) => r.data);
export const deleteConversion = (id) =>
  api.delete(`/conversions/${id}`).then((r) => r.data);
export const pixelUrl = () => `${API}/conversions/pixel.js`;
export const conversionWebhookUrl = () => `${API}/conversions`;

// Export
export const exportCsvUrl = (adSetId) => `${API}/ad-sets/${adSetId}/export.csv`;
export const exportZipUrl = (adSetId) => `${API}/ad-sets/${adSetId}/export.zip`;

// Per-affiliate-link history
export const linkAdSets = (linkId) =>
  api.get(`/affiliate-links/${linkId}/ad-sets`).then((r) => r.data);

// UTM builder
export const updateUtm = (adSetId, cfg) =>
  api.patch(`/ad-sets/${adSetId}/utm`, cfg).then((r) => r.data);
export const resetUtm = (adSetId) =>
  api.delete(`/ad-sets/${adSetId}/utm`).then((r) => r.data);
export const utmPreview = (adSetId) =>
  api.get(`/ad-sets/${adSetId}/utm/preview`).then((r) => r.data);

// Cost estimator
export const costEstimate = (adSetId) =>
  api.get(`/ad-sets/${adSetId}/cost-estimate`).then((r) => r.data);

// Budget
export const getBudget = () => api.get("/budget").then((r) => r.data);
export const updateBudget = (payload) =>
  api.put("/budget", payload).then((r) => r.data);
export const budgetStatus = () =>
  api.get("/budget/status").then((r) => r.data);
export const resetBudgetAlert = () =>
  api.post("/budget/reset-alert").then((r) => r.data);

// Schedules (recurring auto-generation)
export const listSchedules = () =>
  api.get("/schedules").then((r) => r.data);
export const createSchedule = (payload) =>
  api.post("/schedules", payload).then((r) => r.data);
export const updateSchedule = (id, payload) =>
  api.patch(`/schedules/${id}`, payload).then((r) => r.data);
export const deleteSchedule = (id) =>
  api.delete(`/schedules/${id}`).then((r) => r.data);
export const runScheduleNow = (id) =>
  api.post(`/schedules/${id}/run-now`).then((r) => r.data);

// Demo mode
export const demoStatus = () =>
  api.get("/demo/status").then((r) => r.data);
export const demoSeed = () =>
  api.post("/demo/seed").then((r) => r.data);
export const demoReset = () =>
  api.post("/demo/reset").then((r) => r.data);

// API Connections (third-party pulls)
export const listConnections = () =>
  api.get("/connections").then((r) => r.data);
export const createConnection = (payload) =>
  api.post("/connections", payload).then((r) => r.data);
export const updateConnection = (id, payload) =>
  api.patch(`/connections/${id}`, payload).then((r) => r.data);
export const deleteConnection = (id) =>
  api.delete(`/connections/${id}`).then((r) => r.data);
export const testConnection = (id) =>
  api.post(`/connections/${id}/test`).then((r) => r.data);
export const generateFromConnection = (id, payload) =>
  api.post(`/connections/${id}/generate-ads`, payload).then((r) => r.data);

// Geo targeting
export const updateGeo = (adSetId, cfg) =>
  api.patch(`/ad-sets/${adSetId}/geo`, cfg).then((r) => r.data);
export const resetGeo = (adSetId) =>
  api.delete(`/ad-sets/${adSetId}/geo`).then((r) => r.data);
export const updateVariantGeo = (adSetId, payload) =>
  api.patch(`/ad-sets/${adSetId}/variants/geo`, payload).then((r) => r.data);
export const geoAnalytics = (adSetId) =>
  api.get(`/ad-sets/${adSetId}/geo-analytics`).then((r) => r.data);

export const redirectUrl = (id) => `${API}/r/${id}`;
export const variantTrackUrl = (adSetId, platform, variantId) =>
  `${API}/rv/${adSetId}/${platform}/${variantId}`;


// Leads (lead slips = survey submissions)
export const listLeads = (params = {}) =>
  api.get("/leads", { params }).then((r) => r.data);
export const leadsCsvUrl = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return `${API}/leads/export.csv${qs ? `?${qs}` : ""}`;
};


// DMARC reports
export const listDmarcReports = () => api.get("/dmarc/reports").then((r) => r.data);
export const getDmarcReport = (id) => api.get(`/dmarc/reports/${id}`).then((r) => r.data);
export const uploadDmarcReport = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post("/dmarc/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const deleteDmarcReport = (id) => api.delete(`/dmarc/reports/${id}`).then((r) => r.data);
